import { useState } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import TemplateEditor from './TemplateEditor';

const GET_TEMPLATES = gql`
  query GetTemplates($userId: Int) {
    templates(userId: $userId) {
      id
      name
      message
      buttonText
      buttonUrl
      isActive
      createdAt
    }
  }
`;

const GET_WHATSAPP_SESSIONS = gql`
  query GetUserWhatsAppSessions($userId: String!) {
    userWhatsappSessions(userId: $userId) {
      sessionId
      status
      phoneNumber
    }
  }
`;

const GET_BULK_MESSAGES = gql`
  query GetBulkMessages($userId: Int) {
    bulkMessages(userId: $userId) {
      id
      totalContacts
      sentCount
      failedCount
      status
      message
      createdAt
      completedAt
    }
  }
`;

const GET_CONTACT_GROUPS = gql`
  query GetContactGroups {
    contactGroups {
      id
      name
      description
      contactCount
    }
  }
`;

const GET_CONTACTS = gql`
  query GetContacts($page: Int, $pageSize: Int, $groupId: Int) {
    contacts(page: $page, pageSize: $pageSize, groupId: $groupId) {
      contacts {
        id
        name
        phone
        email
        groups {
          id
          name
        }
      }
      total
    }
  }
`;

const CREATE_TEMPLATE = gql`
  mutation CreateTemplate($input: CreateTemplateInput!) {
    createTemplate(input: $input) {
      id
      name
      message
      buttonText
      buttonUrl
    }
  }
`;

const UPDATE_TEMPLATE = gql`
  mutation UpdateTemplate($id: Int!, $input: CreateTemplateInput!) {
    updateTemplate(id: $id, input: $input) {
      id
      name
      message
    }
  }
`;

const DELETE_TEMPLATE = gql`
  mutation DeleteTemplate($id: Int!) {
    deleteTemplate(id: $id)
  }
`;

const SEND_BULK_MESSAGE = gql`
  mutation SendBulkMessage($input: SendBulkMessageInput!) {
    sendBulkMessage(input: $input) {
      id
      totalContacts
      status
    }
  }
`;

const BulkMessaging = () => {
  const user = useSelector((state) => state.auth.user);
  const [activeTab, setActiveTab] = useState('templates');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const [templateForm, setTemplateForm] = useState({
    name: '',
    message: '',
    buttonText: '',
    buttonUrl: ''
  });

  const [sendForm, setSendForm] = useState({
    templateId: '',
    sessionId: '',
    contacts: '',
    message: '',
    buttonText: '',
    buttonUrl: '',
    recipientType: 'manual', // 'manual', 'contactGroup', 'whatsappGroup'
    selectedContactGroups: [],
    selectedContacts: [],
    whatsappGroups: ''
  });

  const { data: templatesData, refetch: refetchTemplates } = useQuery(GET_TEMPLATES, {
    variables: { userId: parseInt(user?.id) },
    skip: !user?.id
  });

  const { data: sessionsData } = useQuery(GET_WHATSAPP_SESSIONS, {
    variables: { userId: user?.id?.toString() || '1' },
    skip: !user?.id
  });

  const { data: bulkData, refetch: refetchBulk } = useQuery(GET_BULK_MESSAGES, {
    variables: { userId: parseInt(user?.id) },
    skip: !user?.id,
    pollInterval: 5000 // Refresh every 5 seconds
  });

  const { data: contactGroupsData } = useQuery(GET_CONTACT_GROUPS, {
    skip: !user?.id
  });

  const { data: contactsData } = useQuery(GET_CONTACTS, {
    variables: { page: 1, pageSize: 10000 }, // Fetch all contacts
    skip: !user?.id
  });

  const [createTemplate] = useMutation(CREATE_TEMPLATE);
  const [updateTemplate] = useMutation(UPDATE_TEMPLATE);
  const [deleteTemplate] = useMutation(DELETE_TEMPLATE);
  const [sendBulkMessage] = useMutation(SEND_BULK_MESSAGE);

  const connectedSessions = sessionsData?.userWhatsappSessions?.filter(s => s.status === 'connected') || [];
  const templates = templatesData?.templates || [];
  const bulkMessages = bulkData?.bulkMessages || [];
  const contactGroups = contactGroupsData?.contactGroups || [];
  const contacts = contactsData?.contacts?.contacts || [];

  const handleCreateTemplate = async (templateData) => {
    try {
      if (editingTemplate) {
        await updateTemplate({
          variables: {
            id: editingTemplate.id,
            input: { ...templateData, userId: parseInt(user?.id) }
          }
        });
      } else {
        await createTemplate({
          variables: {
            input: { ...templateData, userId: parseInt(user?.id) }
          }
        });
      }
      setShowTemplateModal(false);
      setTemplateForm({ name: '', message: '', buttonText: '', buttonUrl: '' });
      setEditingTemplate(null);
      refetchTemplates();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      message: template.message,
      buttonText: template.buttonText || '',
      buttonUrl: template.buttonUrl || ''
    });
    setShowTemplateModal(true);
  };

  const handleDeleteTemplate = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await deleteTemplate({ variables: { id } });
        refetchTemplates();
      } catch (err) {
        alert('Error: ' + err.message);
      }
    }
  };

  const handleSendBulk = async (e) => {
    e.preventDefault();

    if (!sendForm.sessionId) {
      alert('Please select a WhatsApp session');
      return;
    }

    let contactsArray = [];

    // Build contacts array based on recipient type
    if (sendForm.recipientType === 'manual') {
      contactsArray = sendForm.contacts
        .split('\n')
        .map(c => c.trim())
        .filter(c => c.length > 0);
    } else if (sendForm.recipientType === 'contactGroup') {
      // Check if groups are selected
      if (sendForm.selectedContactGroups.length === 0) {
        alert('Please select at least one contact group');
        return;
      }

      // Get all contacts from selected groups
      const selectedGroupContacts = contacts.filter(contact =>
        sendForm.selectedContactGroups.some(groupId =>
          contact.groups?.some(g => g.id === groupId)
        )
      );

      console.log('Selected groups:', sendForm.selectedContactGroups);
      console.log('All contacts:', contacts.length);
      console.log('Filtered contacts:', selectedGroupContacts.length);

      contactsArray = selectedGroupContacts.map(c => c.phone).filter(p => p);

      if (contactsArray.length === 0) {
        alert('No contacts found in the selected groups. Please add contacts to these groups first.');
        return;
      }
    } else if (sendForm.recipientType === 'selectedContacts') {
      if (sendForm.selectedContacts.length === 0) {
        alert('Please select at least one contact');
        return;
      }

      contactsArray = sendForm.selectedContacts.map(id => {
        const contact = contacts.find(c => c.id === id);
        return contact?.phone;
      }).filter(p => p);
    } else if (sendForm.recipientType === 'whatsappGroup') {
      contactsArray = sendForm.whatsappGroups
        .split('\n')
        .map(c => c.trim())
        .filter(c => c.length > 0);
    }

    if (contactsArray.length === 0) {
      alert('Please select at least one recipient');
      return;
    }

    try {
      const input = {
        userId: parseInt(user?.id),
        sessionId: sendForm.sessionId,
        contacts: contactsArray
      };

      if (sendForm.templateId) {
        input.templateId = parseInt(sendForm.templateId);
      } else {
        if (!sendForm.message) {
          alert('Please enter a message or select a template');
          return;
        }
        input.message = sendForm.message;
        input.buttonText = sendForm.buttonText;
        input.buttonUrl = sendForm.buttonUrl;
      }

      await sendBulkMessage({ variables: { input } });
      setShowSendModal(false);
      setSendForm({
        templateId: '',
        sessionId: '',
        contacts: '',
        message: '',
        buttonText: '',
        buttonUrl: '',
        recipientType: 'manual',
        selectedContactGroups: [],
        selectedContacts: [],
        whatsappGroups: ''
      });
      refetchBulk();
      alert('Bulk message sending started! Check the history tab for progress.');
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleTemplateSelect = (e) => {
    const templateId = e.target.value;
    setSendForm({ ...sendForm, templateId });

    if (templateId) {
      const template = templates.find(t => t.id === parseInt(templateId));
      if (template) {
        setSendForm({
          ...sendForm,
          templateId,
          message: template.message,
          buttonText: template.buttonText || '',
          buttonUrl: template.buttonUrl || ''
        });
      }
    }
  };

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 md:p-8 mb-8 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-bulky-blue-500/5 rounded-full blur-3xl -mr-32 -mt-32"></div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 relative z-10">
          <div>
            <h2 className="text-2xl font-medium text-bulky-navy-800 tracking-tight">Bulk Messaging</h2>
            <p className="text-gray-400 text-[10px] font-normal uppercase tracking-wider">Manage your message campaigns</p>
          </div>
          <button
            onClick={() => setShowSendModal(true)}
            className="w-full sm:w-auto bg-bulky-blue-500 hover:bg-bulky-blue-600 text-white font-normal px-6 py-3.5 rounded-2xl transition-all duration-300 shadow-xl shadow-bulky-blue-500/20 active:scale-95 flex items-center justify-center gap-2 text-xs"
          >
            <span className="material-icons-round text-lg">rocket_launch</span>
            <span>Send Campaign</span>
          </button>
        </div>
      </div>


      {/* Tabs */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 mb-8 overflow-hidden">
        <div className="flex bg-gray-50/50 p-2">
          <button
            onClick={() => setActiveTab('templates')}
            className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-normal transition-all duration-300 ${activeTab === 'templates'
              ? 'bg-white text-bulky-blue-500 shadow-lg shadow-gray-200/50'
              : 'text-gray-400 hover:text-bulky-navy-500'
              }`}
          >
            <span className="material-icons-round text-lg">segment</span>
            <span>Templates ({templates.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-normal transition-all duration-300 ${activeTab === 'history'
              ? 'bg-white text-bulky-blue-500 shadow-lg shadow-gray-200/50'
              : 'text-gray-400 hover:text-bulky-navy-500'
              }`}
          >
            <span className="material-icons-round text-lg">history</span>
            <span>Campaign Logs ({bulkMessages.length})</span>
          </button>
        </div>


        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="p-4 md:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
              <h2 className="text-lg font-medium text-bulky-navy-800 tracking-tight">Message Templates</h2>
              <button
                onClick={() => {
                  setEditingTemplate(null);
                  setTemplateForm({ name: '', message: '', buttonText: '', buttonUrl: '' });
                  setShowTemplateModal(true);
                }}
                className="w-full sm:w-auto bg-bulky-navy-800 hover:bg-bulky-navy-900 text-white font-normal px-5 py-3 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 text-xs uppercase tracking-wider"
              >
                <span className="material-icons-round text-sm">add</span>
                <span>Create Template</span>
              </button>
            </div>


            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
                <div key={template.id} className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-bulky-blue-500 hover:shadow-2xl hover:shadow-bulky-blue-500/10 transition-all duration-500 group relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-16 h-16 bg-bulky-blue-50 rounded-bl-[2rem] flex items-center justify-center transition-colors group-hover:bg-bulky-blue-500 text-bulky-blue-500 group-hover:text-white">
                    <span className="material-icons-round text-lg">description</span>
                  </div>
                  <h2 className="text-lg font-medium text-bulky-navy-800 tracking-tight transition-colors group-hover:text-bulky-blue-600 mb-2">{template.name}</h2>
                  <p className="text-gray-500 text-sm mb-6 line-clamp-3 leading-relaxed font-normal">{template.message}</p>

                  {template.buttonText && (
                    <div className="bg-bulky-blue-50/50 rounded-xl p-3 mb-6 border border-bulky-blue-100/50">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="material-icons-round text-xs text-bulky-blue-500 font-normal">link</span>
                        <p className="text-[10px] text-bulky-blue-800 font-normal uppercase tracking-wider leading-none">Call to Action</p>
                      </div>
                      <p className="text-xs text-bulky-navy-700 font-normal truncate">{template.buttonText}</p>
                    </div>
                  )}

                  <div className="flex gap-3 mt-auto">
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className="flex-1 bg-gray-50 hover:bg-bulky-blue-50 text-gray-500 hover:text-bulky-blue-500 font-normal py-3 rounded-xl transition-all text-xs flex items-center justify-center gap-2"
                    >
                      <span className="material-icons-round text-sm">edit</span>
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="flex-1 bg-gray-50 hover:bg-red-50 text-gray-500 hover:text-red-500 font-normal py-3 rounded-xl transition-all text-xs flex items-center justify-center gap-2"
                    >
                      <span className="material-icons-round text-sm">delete_outline</span>
                      <span>Remove</span>
                    </button>
                  </div>
                </div>
              ))}


              {templates.length === 0 && (
                <div className="col-span-full text-center py-12 text-gray-500">
                  <p className="text-lg">No templates yet. Create your first template!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="p-6">
            <h2 className="text-lg font-medium text-bulky-navy-800 tracking-tight mb-6">Bulk Message History</h2>

            <div className="space-y-4">
              {bulkMessages.map((bulk) => (
                <div key={bulk.id} className="bg-white rounded-2xl p-5 md:p-6 border border-gray-100 hover:shadow-lg transition-all duration-300">
                  <div className="flex flex-col md:flex-row justify-between items-start gap-4 md:gap-6">
                    <div className="flex-1 w-full overflow-hidden">
                      <p className="text-sm text-bulky-navy-800 font-normal mb-4 line-clamp-1 flex items-center gap-2">
                        <span className="material-icons-round text-bulky-blue-500 text-sm">chat</span>
                        {bulk.message}
                      </p>
                      <div className="flex flex-wrap gap-2 md:gap-4">
                        <div className="bg-gray-50 px-3 py-1.5 rounded-lg flex items-center gap-2">
                          <span className="material-icons-round text-gray-400 text-xs">group</span>
                          <span className="text-[10px] font-normal uppercase tracking-wider text-gray-400">Total: <span className="text-bulky-navy-800">{bulk.totalContacts}</span></span>
                        </div>
                        <div className="bg-green-50 px-3 py-1.5 rounded-lg flex items-center gap-2">
                          <span className="material-icons-round text-green-500 text-xs">check_circle</span>
                          <span className="text-[10px] font-normal uppercase tracking-wider text-green-600">Sent: <span className="text-green-700">{bulk.sentCount}</span></span>
                        </div>
                        <div className="bg-red-50 px-3 py-1.5 rounded-lg flex items-center gap-2">
                          <span className="material-icons-round text-red-500 text-xs">error</span>
                          <span className="text-[10px] font-normal uppercase tracking-wider text-red-600">Fail: <span className="text-red-700">{bulk.failedCount}</span></span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-row md:flex-col justify-between items-center md:items-end w-full md:w-auto">
                      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-normal uppercase tracking-wider ${bulk.status === 'completed' ? 'bg-green-500 text-white shadow-lg shadow-green-500/20' :
                        bulk.status === 'processing' ? 'bg-yellow-500 text-white shadow-lg shadow-yellow-500/20' :
                          bulk.status === 'failed' ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' :
                            'bg-gray-400 text-white'
                        }`}>
                        <span className="material-icons-round text-[10px] animate-pulse">
                          {bulk.status === 'completed' ? 'done_all' : bulk.status === 'processing' ? 'sync' : 'error'}
                        </span>
                        {bulk.status}
                      </div>
                      <p className="text-[10px] text-gray-400 font-normal uppercase tracking-tighter md:mt-3 flex items-center gap-1">
                        <span className="material-icons-round text-[10px]">access_time</span>
                        {new Date(bulk.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}


              {bulkMessages.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg">No bulk messages sent yet.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-bulky-navy-900/60 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-6xl my-8 overflow-hidden">
            <div className="bg-bulky-navy-500 p-8 text-center text-white">
              <h2 className="text-2xl font-medium text-white tracking-tight">
                {editingTemplate ? 'Template Optimization' : 'Blueprint Design'}
              </h2>
              <p className="text-bulky-navy-200 text-xs font-normal uppercase tracking-wider mt-1">
                {editingTemplate ? 'Refine your messaging strategy' : 'Compose a new distribution pattern'}
              </p>
            </div>


            <div className="p-6">
              <TemplateEditor
                initialData={editingTemplate || templateForm}
                onSave={handleCreateTemplate}
                onCancel={() => {
                  setShowTemplateModal(false);
                  setEditingTemplate(null);
                  setTemplateForm({ name: '', message: '', buttonText: '', buttonUrl: '' });
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Send Bulk Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-bulky-navy-900/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-white/20 animate-in zoom-in duration-300">
            <div className="bg-bulky-navy-500 p-10 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-bulky-blue-500/20 rounded-full blur-2xl -mr-16 -mt-16"></div>
              <div className="relative z-10">
                <h2 className="text-2xl font-medium text-white tracking-tight">Launch Campaign</h2>
                <p className="text-bulky-navy-200 text-xs font-normal uppercase tracking-[0.3em] mt-2">Bulk Distribution Engine</p>
              </div>
            </div>


            <form onSubmit={handleSendBulk} className="p-10 flex-1 overflow-y-auto custom-scrollbar">
              <div className="mb-6">
                <label className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">WhatsApp Entry Point</label>
                <div className="relative">
                  <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">phonelink_ring</span>
                  <select
                    value={sendForm.sessionId}
                    onChange={(e) => setSendForm({ ...sendForm, sessionId: e.target.value })}
                    required
                    className="w-full pl-12 pr-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-700 appearance-none"
                  >
                    <option value="">Select an active session</option>
                    {connectedSessions.map((session) => (
                      <option key={session.sessionId} value={session.sessionId}>
                        {session.sessionId} ({session.phoneNumber})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">Choose Template</label>
                <div className="relative">
                  <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">description</span>
                  <select
                    value={sendForm.templateId}
                    onChange={handleTemplateSelect}
                    className="w-full pl-12 pr-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-700 appearance-none"
                  >
                    <option value="">Use Custom Message Below</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">Final Message Body</label>
                <div className="relative">
                  <span className="material-icons-round absolute left-4 top-5 text-gray-400">message</span>
                  <textarea
                    value={sendForm.message}
                    onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
                    placeholder="Message will appear here after selecting template..."
                    rows="4"
                    required={!sendForm.templateId}
                    className="w-full pl-12 pr-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-700 resize-none leading-relaxed"
                  />
                </div>
              </div>

              <div className="bg-bulky-blue-50/50 border border-bulky-blue-100 rounded-2xl p-6 mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <span className="material-icons-round text-bulky-blue-500">add_link</span>
                  <h2 className="text-sm font-medium text-bulky-blue-800 uppercase tracking-wider">Interactive Component (Optional)</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-bulky-blue-300 text-sm">text_fields</span>
                    <input
                      type="text"
                      value={sendForm.buttonText}
                      onChange={(e) => setSendForm({ ...sendForm, buttonText: e.target.value })}
                      placeholder="Button Label"
                      className="w-full pl-10 pr-4 py-3 bg-white border border-bulky-blue-100 rounded-xl focus:border-bulky-blue-500 focus:outline-none transition-all text-sm font-normal text-bulky-navy-700"
                    />
                  </div>
                  <div className="relative">
                    <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-bulky-blue-300 text-sm">link</span>
                    <input
                      type="url"
                      value={sendForm.buttonUrl}
                      onChange={(e) => setSendForm({ ...sendForm, buttonUrl: e.target.value })}
                      placeholder="Redirect URL"
                      className="w-full pl-10 pr-4 py-3 bg-white border border-bulky-blue-100 rounded-xl focus:border-bulky-blue-500 focus:outline-none transition-all text-sm font-normal text-bulky-navy-700"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">Recipient Type</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <button
                    type="button"
                    onClick={() => setSendForm({ ...sendForm, recipientType: 'manual' })}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center justify-center text-center ${sendForm.recipientType === 'manual'
                      ? 'border-bulky-blue-500 bg-bulky-blue-50 text-bulky-blue-600'
                      : 'border-gray-200 hover:border-gray-300 text-gray-500'
                      }`}
                  >
                    <span className="material-icons-round text-2xl mb-2">edit_note</span>
                    <p className="text-[10px] font-normal uppercase tracking-tighter">Manual</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSendForm({ ...sendForm, recipientType: 'selectedContacts' })}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center justify-center text-center ${sendForm.recipientType === 'selectedContacts'
                      ? 'border-bulky-blue-500 bg-bulky-blue-50 text-bulky-blue-600'
                      : 'border-gray-200 hover:border-gray-300 text-gray-500'
                      }`}
                  >
                    <span className="material-icons-round text-2xl mb-2">person</span>
                    <p className="text-[10px] font-normal uppercase tracking-tighter">Contacts</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSendForm({ ...sendForm, recipientType: 'contactGroup' })}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center justify-center text-center ${sendForm.recipientType === 'contactGroup'
                      ? 'border-bulky-blue-500 bg-bulky-blue-50 text-bulky-blue-600'
                      : 'border-gray-200 hover:border-gray-300 text-gray-500'
                      }`}
                  >
                    <span className="material-icons-round text-2xl mb-2">groups</span>
                    <p className="text-[10px] font-normal uppercase tracking-tighter">Groups</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSendForm({ ...sendForm, recipientType: 'whatsappGroup' })}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center justify-center text-center ${sendForm.recipientType === 'whatsappGroup'
                      ? 'border-bulky-blue-500 bg-bulky-blue-50 text-bulky-blue-600'
                      : 'border-gray-200 hover:border-gray-300 text-gray-500'
                      }`}
                  >
                    <span className="material-icons-round text-2xl mb-2">forum</span>
                    <p className="text-[10px] font-normal uppercase tracking-tighter">WA Groups</p>
                  </button>
                </div>
              </div>

              {/* Manual Entry */}
              {sendForm.recipientType === 'manual' && (
                <div className="mb-8">
                  <label className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">Phone Numbers (One per line)</label>
                  <div className="relative">
                    <span className="material-icons-round absolute left-4 top-5 text-gray-400">group_add</span>
                    <textarea
                      value={sendForm.contacts}
                      onChange={(e) => setSendForm({ ...sendForm, contacts: e.target.value })}
                      placeholder="919106649835&#10;916353549828"
                      rows="6"
                      required
                      className="w-full pl-12 pr-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-700 resize-none font-mono text-xs"
                    />
                  </div>
                  <p className="mt-2 text-[10px] text-gray-400 font-normal uppercase tracking-tighter">Format: country_code + number</p>
                </div>
              )}

              {/* Select Individual Contacts */}
              {sendForm.recipientType === 'selectedContacts' && (
                <div className="mb-8">
                  <label className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">
                    Select Contacts ({sendForm.selectedContacts.length} selected)
                  </label>
                  <div className="bg-gray-50 rounded-2xl p-4 max-h-64 overflow-y-auto">
                    {contacts.length === 0 ? (
                      <p className="text-center text-gray-400 py-4">No contacts available</p>
                    ) : (
                      <div className="space-y-2">
                        {contacts.map((contact) => (
                          <label key={contact.id} className="flex items-center gap-3 p-3 bg-white rounded-xl hover:bg-bulky-blue-50 cursor-pointer transition-all">
                            <input
                              type="checkbox"
                              checked={sendForm.selectedContacts.includes(contact.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSendForm({
                                    ...sendForm,
                                    selectedContacts: [...sendForm.selectedContacts, contact.id]
                                  });
                                } else {
                                  setSendForm({
                                    ...sendForm,
                                    selectedContacts: sendForm.selectedContacts.filter(id => id !== contact.id)
                                  });
                                }
                              }}
                              className="w-4 h-4 text-bulky-blue-500 rounded"
                            />
                            <div className="flex-1">
                              <p className="font-normal text-sm text-bulky-navy-700">{contact.name}</p>
                              <p className="text-xs text-gray-400">{contact.phone}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Groups */}
              {sendForm.recipientType === 'contactGroup' && (
                <div className="mb-8">
                  <label className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">
                    Select Contact Groups ({sendForm.selectedContactGroups.length} selected)
                  </label>
                  <div className="bg-gray-50 rounded-2xl p-4 max-h-64 overflow-y-auto">
                    {contactGroups.length === 0 ? (
                      <p className="text-center text-gray-400 py-4">No contact groups available</p>
                    ) : (
                      <div className="space-y-2">
                        {contactGroups.map((group) => (
                          <label key={group.id} className="flex items-center gap-3 p-3 bg-white rounded-xl hover:bg-bulky-blue-50 cursor-pointer transition-all">
                            <input
                              type="checkbox"
                              checked={sendForm.selectedContactGroups.includes(group.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSendForm({
                                    ...sendForm,
                                    selectedContactGroups: [...sendForm.selectedContactGroups, group.id]
                                  });
                                } else {
                                  setSendForm({
                                    ...sendForm,
                                    selectedContactGroups: sendForm.selectedContactGroups.filter(id => id !== group.id)
                                  });
                                }
                              }}
                              className="w-4 h-4 text-bulky-blue-500 rounded"
                            />
                            <div className="flex-1">
                              <p className="font-normal text-sm text-bulky-navy-700">{group.name}</p>
                              <p className="text-xs text-gray-400">{group.contactCount} contacts</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* WhatsApp Groups */}
              {sendForm.recipientType === 'whatsappGroup' && (
                <div className="mb-8">
                  <label className="block text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">WhatsApp Group IDs (One per line)</label>
                  <div className="relative">
                    <span className="material-icons-round absolute left-4 top-5 text-gray-400">forum</span>
                    <textarea
                      value={sendForm.whatsappGroups}
                      onChange={(e) => setSendForm({ ...sendForm, whatsappGroups: e.target.value })}
                      placeholder="120363123456789012@g.us&#10;120363987654321098@g.us"
                      rows="6"
                      required
                      className="w-full pl-12 pr-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-700 resize-none font-mono text-xs"
                    />
                  </div>
                  <p className="mt-2 text-[10px] text-gray-400 font-normal uppercase tracking-tighter">Format: group_id@g.us</p>
                </div>
              )}

              <div className="flex gap-4 sticky bottom-0 bg-white pt-4 pb-2 border-t border-gray-50">
                <button
                  type="button"
                  onClick={() => {
                    setShowSendModal(false);
                    setSendForm({
                      templateId: '',
                      sessionId: '',
                      contacts: '',
                      message: '',
                      buttonText: '',
                      buttonUrl: '',
                      recipientType: 'manual',
                      selectedContactGroups: [],
                      selectedContacts: [],
                      whatsappGroups: ''
                    });
                  }}
                  className="flex-1 bg-gray-50 hover:bg-gray-100 text-gray-400 font-normal py-4 rounded-2xl transition-all active:scale-95"
                >
                  Discard
                </button>
                <button
                  type="submit"
                  className="flex-[2] bg-bulky-blue-500 hover:bg-bulky-blue-600 text-white font-normal py-4 rounded-2xl transition-all shadow-xl shadow-bulky-blue-500/20 active:scale-95 flex items-center justify-center gap-2"
                >
                  <span className="material-icons-round">rocket_launch</span>
                  <span>Initiate Broadcast</span>
                </button>
              </div>
            </form>

          </div>
        </div>
      )}
    </div>
  );
};

export default BulkMessaging;
